import {ColumnsConfig, ColumnsType, RowDataType, TableState} from './actions';
import {Map} from 'immutable';
import {createSelector} from 'reselect';
import * as _ from 'lodash';
import {getTypedFieldValidationFunction} from 'cl-form/validation/validators';
import {isChildRow} from '../VirtualizedHelpers';

const getData = (tableState: TableState): Map<number, RowDataType> => tableState.data;
const getDataUpdates = (tableState: TableState): Map<number, RowDataType> => tableState.dataUpdates;
export const getColumns = (tableState: TableState): ColumnsType => tableState.columns;
const getColumnsSorter = (tableState: TableState): ((columns) => string[]) =>
  tableState.columnsConfig ? tableState.columnsConfig.columnsSorter : undefined;
const getColumnsConfig = (tableState: TableState): ColumnsConfig => tableState.columnsConfig;

export const getUpdatedData = createSelector(
  [getData, getDataUpdates],
  (data, dataUpdates) =>
    data
      ? data.withMutations(data => {
          dataUpdates.forEach((rowDataUpdates, index) => {
            if (!_.isEmpty(rowDataUpdates)) {
              data.update(index, rowData => ({...rowData, ...rowDataUpdates}));
            }
          });
        })
      : undefined
);

export const getValidData = (tableState: TableState): Map<number, RowDataType> => {
  const columns = getColumns(tableState);
  const columnsConfig = getColumnsConfig(tableState);
  const updatedData = getUpdatedData(tableState);
  const status = _.isFunction(columnsConfig.status) ? columnsConfig.status : undefined;
  return updatedData.filter(rowData => {
    return (
      (_.isUndefined(status) || !status(rowData, tableState).error) &&
      !_.some(columns, (columnInfo, columnName) => {
        const error =
          !isValidationDisabled(columnInfo, rowData, updatedData) &&
          getTypedFieldValidationFunction(columnName, columnInfo)(rowData[columnName], rowData, updatedData);
        return error ? error : false;
      })
    );
  }) as Map<number, RowDataType>;
};

export const getNonChildrenCount = createSelector(
  [getColumnsConfig, getUpdatedData],
  (columnsConfig, updatedData) => {
    return getNonChildrenData(columnsConfig, updatedData).count();
  }
);

export const getNonChildrenValidDataCount = createSelector(
  [getColumnsConfig, getValidData],
  (columnsConfig, validData) => {
    return getNonChildrenData(columnsConfig, validData).count();
  }
);

const getNonChildrenData = (columnsConfig: ColumnsConfig, updatedData: Map<number, RowDataType>) => {
  return updatedData.filter(rowData => !isChildRow(rowData));
};

export const removeDisabledFieldsFromData = (columns, data) =>
  data.withMutations(data => {
    data.forEach((rowData, index) => {
      _.forEach(columns, ({disabled}, columnName) => {
        if (_.isFunction(disabled) && disabled(rowData, data)) {
          data.update(index, rowData => ({...rowData, [columnName]: null}));
        }
      });
    });
  });

export const getRowFirstError = (
  rowData: RowDataType,
  columns: ColumnsType,
  updatedData: Map<number, RowDataType>
): string => {
  let error: string = null;
  const iteratee = (columnInfo, columnName) => {
    error =
      !isValidationDisabled(columnInfo, rowData, updatedData) &&
      getTypedFieldValidationFunction(columnName, columnInfo)(rowData[columnName], rowData, updatedData);
    if (error) {
      return false;
    }
  };
  _.forEach(_.pickBy(columns, 'hidden'), iteratee); // first check hidden columns for errors as they are only visible in status column.
  if (!error) {
    _.forEach(_.omitBy(columns, 'hidden'), iteratee);
  }
  return error;
};

export const getColumnsWithErrors = createSelector(
  [getColumns, getUpdatedData],
  (columns: ColumnsType, updatedData: Map<number, RowDataType>) => {
    return _.mapValues(columns, (columnInfo, columnName) => {
      return updatedData.some(
        rowData =>
          !isValidationDisabled(columnInfo, rowData, updatedData) &&
          !!getTypedFieldValidationFunction(columnName, columnInfo)(rowData[columnName], rowData, updatedData)
      );
    });
  }
);

export const getColumnsWithRequiredRows = createSelector(
  [getColumns, getUpdatedData],
  (columns: ColumnsType, updatedData: Map<number, RowDataType>) => {
    return _.mapValues(columns, (columnInfo, columnName) => {
      return updatedData.some(
        rowData =>
          !isValidationDisabled(columnInfo, rowData, updatedData) &&
          !!getTypedFieldValidationFunction(columnName, columnInfo)(null, rowData, updatedData)
      ); // required means it will return error for null value
    });
  }
);

export const getVisibleColumns = createSelector(
  [getColumns, getColumnsSorter],
  (columns, sortFunction) => {
    const visibleColumns = _.omitBy(columns, ({hidden}) => hidden);
    return sortFunction ? sortFunction(columns) : _.keys(visibleColumns);
  }
);

const isValidationDisabled = (columnInfo, rowData, updatedData) =>
  _.isFunction(columnInfo.disabled) && columnInfo.disabled(rowData, updatedData);
